import { useMemo } from 'react';

import {
  convertToMeters,
  getLengthUnit,
  isLengthUnit,
  parseValueWithUnit,
} from './utils';
import { useSettings } from '../Settings/SettingsProvider';
import { Units } from '@ais-ui/utils';

export interface LengthResult {
  value: number;
  unit: string;
  abbreviation: string;
}

// Conversion constants (all relative to meters)
const CONVERSIONS = {
  // Metric
  METERS_TO_CM: 100,
  METERS_TO_KM: 0.001,

  // Imperial
  METERS_TO_FEET: 3.28084,
  FEET_TO_INCHES: 12,
  FEET_TO_MILES: 1 / 5280,

  // Maritime
  METERS_TO_NAUTICAL_MILES: 0.000539957,
};

export const useLengthUnits = () => {
  const { settings } = useSettings();
  const selectedUnit = useMemo(() => getLengthUnit(settings), [settings]);

  const convertToMetric = (valueInMeters: number): LengthResult => {
    // < 1 meter: centimeters
    if (valueInMeters < 1) {
      const cm = valueInMeters * CONVERSIONS.METERS_TO_CM;
      return {
        value: Math.round(cm * 10) / 10, // Round to 1 decimal
        unit: 'centimeters',
        abbreviation: 'cm',
      };
    }

    // ≥ 1000 meters: kilometers
    if (valueInMeters >= 1000) {
      const km = valueInMeters * CONVERSIONS.METERS_TO_KM;
      return {
        value: Math.round(km * 100) / 100, // Round to 2 decimals
        unit: 'kilometers',
        abbreviation: 'km',
      };
    }

    // 1-999 meters: meters
    return {
      value: Math.round(valueInMeters * 10) / 10, // Round to 1 decimal
      unit: 'meters',
      abbreviation: 'm',
    };
  };

  const convertToImperial = (valueInMeters: number): LengthResult => {
    const feet = valueInMeters * CONVERSIONS.METERS_TO_FEET;

    // < 1 foot: inches
    if (feet < 1) {
      const inches = feet * CONVERSIONS.FEET_TO_INCHES;
      return {
        value: Math.round(inches * 10) / 10, // Round to 1 decimal
        unit: 'inches',
        abbreviation: 'in',
      };
    }

    // ≥ 1000 feet: miles
    if (feet >= 1000) {
      const miles = feet * CONVERSIONS.FEET_TO_MILES;
      return {
        value: Math.round(miles * 100) / 100, // Round to 2 decimals
        unit: 'miles',
        abbreviation: 'mi',
      };
    }

    // 1-999 feet: feet
    return {
      value: Math.round(feet * 10) / 10, // Round to 1 decimal
      unit: 'feet',
      abbreviation: 'ft',
    };
  };

  const convertToMaritime = (valueInMeters: number): LengthResult => {
    // < 500 meters: use meters (for vessel dimensions, close-range measurements)
    if (valueInMeters < 500) {
      return {
        value: Math.round(valueInMeters * 10) / 10, // Round to 1 decimal
        unit: 'meters',
        abbreviation: 'm',
      };
    }

    // ≥ 500 meters: use nautical miles (for navigation distances)
    const nauticalMiles = valueInMeters * CONVERSIONS.METERS_TO_NAUTICAL_MILES;
    return {
      value: Math.round(nauticalMiles * 100) / 100, // Round to 2 decimals
      unit: 'nautical miles',
      abbreviation: 'NM',
    };
  };

  const convertLength = (valueInMeters: number): LengthResult => {
    switch (selectedUnit) {
      case Units.METRIC:
        return convertToMetric(valueInMeters);
      case Units.IMPERIAL:
        return convertToImperial(valueInMeters);
      case Units.MARITIME:
        return convertToMaritime(valueInMeters);
      default:
        return convertToMetric(valueInMeters);
    }
  };

  const formatLength = (valueInMeters: number): string => {
    const result = convertLength(valueInMeters);
    return `${result.value} ${result.abbreviation}`;
  };

  const convertLengthString = (valueString: string | undefined): string => {
    // Guard clause: return N/A if parsing failed
    const parsedValue = parseValueWithUnit(valueString);
    if (!parsedValue) {
      return '';
    }

    const { value, unit } = parsedValue;

    // Guard clause: return original if not a length unit
    if (!isLengthUnit(unit)) {
      return `${value} ${unit}`;
    }

    // Convert to meters first, then to user's preferred unit
    const valueInMeters = convertToMeters(value, unit);

    return formatLength(valueInMeters);
  };

  return {
    convertLength,
    formatLength,
    convertLengthString,
    selectedUnit,
  };
};
